import { useEffect, useRef } from 'react';
import { usePlayerAuth } from '../contexts/PlayerAuthContext';
import {
  ensureDefaultPlayerNotificationPrefs,
  syncPlayerTournamentNotifications,
} from '../services/playerNotificationSync';
import { arePlayerNotificationsEnabled, loadPlayerNotificationPrefs } from '../utils/playerNotificationPrefs';

const PERMISSION_PROMPT_KEY = 'player-notif-permission-prompted';

/**
 * Keeps push subscriptions in sync with the player's USCF-linked tournament
 * registrations whenever notification settings are enabled.
 */
const PlayerNotificationBootstrap: React.FC = () => {
  const { isLoggedIn, isLoading, player, registeredTournaments } = usePlayerAuth();
  const syncKeyRef = useRef('');

  useEffect(() => {
    if (!isLoggedIn || !player) {
      syncKeyRef.current = '';
      return;
    }
    if (isLoading) return;

    const syncKey = [
      player.userId,
      player.uscfId || '',
      registeredTournaments.map((t) => `${t.tournamentId}:${t.playerId || ''}`).join('|'),
      JSON.stringify(loadPlayerNotificationPrefs()),
    ].join('::');

    if (syncKeyRef.current === syncKey) return;
    syncKeyRef.current = syncKey;

    const run = async () => {
      ensureDefaultPlayerNotificationPrefs(registeredTournaments.length > 0);

      if (registeredTournaments.length === 0 || !player.uscfId?.trim()) return;
      if (!arePlayerNotificationsEnabled()) {
        await syncPlayerTournamentNotifications(player.name, player.uscfId, registeredTournaments);
        return;
      }

      const shouldPrompt =
        !sessionStorage.getItem(PERMISSION_PROMPT_KEY) && Notification.permission === 'default';

      if (shouldPrompt) {
        sessionStorage.setItem(PERMISSION_PROMPT_KEY, '1');
      }

      await syncPlayerTournamentNotifications(player.name, player.uscfId, registeredTournaments, {
        requestPermission: shouldPrompt,
      });
    };

    void run();
  }, [isLoading, isLoggedIn, player, registeredTournaments]);

  return null;
};

export default PlayerNotificationBootstrap;
